import { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import type { EffectPackCandidateDTO } from '@/api/generated/model';
import { useApproveEffectPack, useRejectEffectPack } from '../api';
import { EffectPackThumbnail } from './EffectPackThumbnail';
import { KindBadge } from './KindBadge';

/**
 * One review-queue row (CMP parity: EffectPackCandidateCard in EffectPackReviewScreen.kt).
 * Approve/Reject each own mutation instance, so `pending` (and the disabled buttons it drives) is
 * naturally scoped to THIS card only — matching the CMP reducer's per-id `pendingActionIds` set.
 */
export function EffectPackCandidateCard({ candidate }: { candidate: EffectPackCandidateDTO }) {
  const [expanded, setExpanded] = useState(false);
  const confirm = useConfirm();
  const approveMutation = useApproveEffectPack();
  const rejectMutation = useRejectEffectPack();

  const id = candidate.id ?? '';
  const displayName = candidate.displayName?.trim() || id || 'Untitled template';
  const category = candidate.category?.trim();
  const pending = approveMutation.isPending || rejectMutation.isPending;

  const onApprove = async () => {
    if (!id) return;
    const { confirmed } = await confirm({
      title: 'Approve template',
      description: `Approve "${displayName}"? It will be promoted out of staging.`,
      confirmText: 'Approve',
      variant: 'default',
    });
    if (!confirmed) return;
    try {
      await approveMutation.mutateAsync({ id });
      toast.success('Template approved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to approve template');
    }
  };

  const onReject = async () => {
    if (!id) return;
    const { confirmed } = await confirm({
      title: 'Reject template',
      description: `Reject "${displayName}"? This removes it from the review queue.`,
      confirmText: 'Reject',
      variant: 'destructive',
    });
    if (!confirmed) return;
    try {
      await rejectMutation.mutateAsync({ id });
      toast.success('Template rejected');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reject template');
    }
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-4">
        <div className="flex items-start gap-4">
          {candidate.hasThumbnail && id ? <EffectPackThumbnail id={id} /> : null}

          <div className="min-w-0 flex-1 space-y-2">
            <p className="truncate text-sm font-semibold">{displayName}</p>

            <div className="flex flex-wrap items-center gap-2">
              <KindBadge kind={candidate.kind ?? ''} />
              {category && <span className="text-xs text-muted-foreground">{category}</span>}
            </div>

            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-xs font-medium text-primary hover:underline"
            >
              {expanded ? 'Hide JSON' : 'Show JSON'}
            </button>

            {expanded && (
              <div className="overflow-x-auto rounded-md border bg-muted/50 p-3">
                <pre className="font-mono text-xs whitespace-pre text-muted-foreground">
                  {candidate.rawJson ?? ''}
                </pre>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-border pt-3">
          <Button variant="destructive" size="sm" disabled={pending || !id} onClick={() => void onReject()}>
            Reject
          </Button>
          <Button size="sm" disabled={pending || !id} onClick={() => void onApprove()}>
            Approve
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
